import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { fetchViralTemplatePageForLanding } from '@/services/viralTemplate';

import { HOME_TEMPLATE_GRID_PAGE_SIZE, type TemplateVideoItem } from './config';
import { mapTplListToTemplateVideos } from './mapTemplateVideosFromApi';

type TemplateListContextValue = {
  templates: TemplateVideoItem[];
};

const TemplateListContext = createContext<TemplateListContextValue>({
  templates: [],
});

/**
 * new-home：爆款模板列表只请求一次，供 Hero「Template」tab 与下方 templateGrid 共用。
 */
export function NewHomeTemplateListProvider({ children }: { children: ReactNode }) {
  const [templates, setTemplates] = useState<TemplateVideoItem[]>([]);

  useEffect(() => {
    const ac = new AbortController();
    void (async () => {
      try {
        const res = await fetchViralTemplatePageForLanding({
          pageNum: 1,
          pageSize: HOME_TEMPLATE_GRID_PAGE_SIZE,
          signal: ac.signal,
        });
        if (ac.signal.aborted) return;
        const mapped =
          res?.success && res.items?.length ? mapTplListToTemplateVideos(res.items) : [];
        if (mapped.length > 0) setTemplates(mapped);
      } catch (e: unknown) {
        if (e instanceof DOMException && e.name === 'AbortError') return;
      }
    })();
    return () => ac.abort();
  }, []);

  const value = useMemo(() => ({ templates }), [templates]);

  return (
    <TemplateListContext.Provider value={value}>{children}</TemplateListContext.Provider>
  );
}

export function useSharedTemplateList(): TemplateListContextValue {
  return useContext(TemplateListContext);
}
